import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SidebarService } from '../../../../core/services/utils/sidebar.service';
import { AgeTierService, AgeTier } from '../../../../core/services/utils/age-tier.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { SpaceService } from '../../../../core/services/utils/space.service';
import { Space, spaceFromUrl } from '../../../../core/access/space';
import { SpaceSwitcherComponent } from '../../space-switcher/space-switcher.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [TranslateModule, CommonModule, RouterLink, RouterLinkActive, SpaceSwitcherComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit {
  links: any[] = [];
  isLTR = true;
  isSidebarOpen = false;
  isCollapsed = false;
  expandedMenus: { [key: string]: boolean } = {};

  userName: string = '';
  userAvatar: string = 'assets/images/default-user.png';

  private translateService = inject(TranslateService);
  private ageTierService = inject(AgeTierService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private spaceService = inject(SpaceService);

  readonly currentTier = this.ageTierService.currentTier;

  /** Les espaces que le jeton ouvre ; le sélecteur n'apparaît qu'à partir de deux. */
  readonly spaces = this.spaceService.available();
  readonly currentSpace = this.spaceService.current;

  /** Le menu suit l'espace de l'URL : changer d'espace, c'est changer de menu. */
  private shownSpace: Space | null = null;

  constructor(private sidebarService: SidebarService) {}

  async ngOnInit(): Promise<void> {
    this.refreshLinks();

    this.sidebarService.links$.subscribe((links) => {
      this.links = links;
      // After links arrive, auto-expand the submenu owning the current route
      this.openSubmenuForCurrentRoute();
    });

    // Re-evaluate on every navigation (e.g. after a refresh + redirect, or
    // when the user navigates programmatically)
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.refreshLinks();
        this.openSubmenuForCurrentRoute();
      });

    this.sidebarService.getSidebarStatus().subscribe((status) => {
      this.isSidebarOpen = status;
    });

    const lang = localStorage.getItem('lang') || 'ar';
    this.translateService.use(lang);
    this.isLTR = lang !== 'ar';

    this.translateService.onLangChange.subscribe((event) => {
      this.isLTR = event.lang !== 'ar';
    });

    // Restore collapsed state
    this.isCollapsed = localStorage.getItem('sidebar-collapsed') === 'true';

    // Load profile (best-effort, no UI break if it fails)
    try {
      this.userName = await this.authService.getUserDisplayName();
      this.userAvatar = await this.authService.getUserAvatar();
    } catch {
      this.userName = '';
    }
  }

  closeSidebar(): void {
    this.sidebarService.closeSidebar();
  }

  switchSpace(space: Space): void {
    this.closeSidebar();
    this.spaceService.switchTo(space);
  }

  /** Recompose le menu quand l'URL change d'espace — pas à chaque navigation. */
  private refreshLinks(): void {
    const space = spaceFromUrl(this.router.url);
    if (space === this.shownSpace && this.links.length) return;
    this.shownSpace = space;
    this.sidebarService.checkSidebarLinks(space);
  }

  toggleSubmenu(linkId: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.isCollapsed) {
      // Expanding submenu while collapsed forces re-expand for usability
      this.isCollapsed = false;
      localStorage.setItem('sidebar-collapsed', 'false');
    }
    this.expandedMenus[linkId] = !this.expandedMenus[linkId];
  }

  isSubmenuExpanded(linkId: string): boolean {
    return this.expandedMenus[linkId] || false;
  }

  /**
   * Returns true if any of the submenu children of `link` matches the
   * current router URL. Used to highlight the parent item even when the
   * user has manually collapsed the submenu.
   */
  hasActiveChild(link: any): boolean {
    if (!link?.hasSubmenu || !Array.isArray(link.submenu)) return false;
    const url = this.router.url.split('?')[0].split('#')[0];
    return link.submenu.some((sub: any) => {
      if (!sub?.link) return false;
      return url === sub.link || url.startsWith(sub.link + '/');
    });
  }

  /**
   * Auto-expand the submenu that contains the current route so the active
   * item stays visible across page refreshes / direct deep links.
   * Existing user-expanded states are preserved (we only ever turn ON,
   * never OFF, so collapsing remains a deliberate user action).
   */
  private openSubmenuForCurrentRoute(): void {
    if (!this.links?.length) return;
    for (const link of this.links) {
      if (this.hasActiveChild(link)) {
        this.expandedMenus[link.id] = true;
      }
    }
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('sidebar-collapsed', String(this.isCollapsed));
    if (this.isCollapsed) {
      // Close all submenus when collapsing for cleaner rail look
      this.expandedMenus = {};
    }
  }

  logout(): void {
    this.authService.logout();
  }

  tierLabelKey(tier: AgeTier): string {
    switch (tier) {
      case 'primaire': return 'AGE_TIER.PRIMAIRE';
      case 'college':  return 'AGE_TIER.COLLEGE';
      case 'lycee':    return 'AGE_TIER.LYCEE';
    }
  }

  tierIcon(tier: AgeTier): string {
    switch (tier) {
      case 'primaire': return 'fa-puzzle-piece';
      case 'college':  return 'fa-rocket';
      case 'lycee':    return 'fa-graduation-cap';
    }
  }
}
